import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import Intercom, { onHide, onShow, shutdown } from "@intercom/messenger-js-sdk";

import { theme } from "config/theme";
import { getAuthCookie } from "helpers/auth-cookie";
import { IReduxStore } from "interfaces/IReduxStore";
import { parseJwt } from "utils/parse-jwt";

import { setIntercomMessengerOpenFromSdk, startIntercomLauncherPaddingSync } from "./styles";

const INTERCOM_APP_ID = "y950tbam";

const B2C_BRAND_SLUGS = ["proenem", "promedicina", "promilitares"];

const BRAND_USER_ID_PREFIX: Record<string, string> = {
    proenem: "pe-",
    promilitares: "pm-",
    promedicina: "pmed-"
};

const getIntercomUserId = (brandSlug: string): string | null => {
    const prefix = BRAND_USER_ID_PREFIX[brandSlug];

    if (!prefix) {
        return null;
    }

    const token = getAuthCookie()?.token;

    if (!token) {
        return null;
    }

    try {
        const sub = parseJwt(token)?.sub;

        if (sub === undefined || sub === null || sub === "") {
            return null;
        }

        return `${prefix}${sub}`;
    } catch {
        return null;
    }
};

const IntercomMessenger = () => {
    const intercomPaddingHandlersRegistered = useRef(false);
    const isB2CBrand = B2C_BRAND_SLUGS.includes(theme.project.slug);
    const { token, name, email } = useSelector(({ auth, credentials }: IReduxStore) => ({
        token: auth.token,
        name: credentials.name || auth.credentials.name,
        email: credentials.email || auth.credentials.email
    }));

    useEffect(() => {
        const userId = getIntercomUserId(theme.project.slug);

        if (!isB2CBrand || !token || !userId) {
            return;
        }

        Intercom({
            app_id: INTERCOM_APP_ID,
            user_id: userId,
            name,
            email,
            Plataforma: "Legada"
        });

        if (!intercomPaddingHandlersRegistered.current) {
            onShow(() => setIntercomMessengerOpenFromSdk(true));
            onHide(() => setIntercomMessengerOpenFromSdk(false));
            intercomPaddingHandlersRegistered.current = true;
        }

        const stopLauncherPaddingSync = startIntercomLauncherPaddingSync();

        return () => {
            stopLauncherPaddingSync();
        };
    }, [isB2CBrand, token, name, email]);

    useEffect(() => {
        if (!isB2CBrand) {
            return;
        }

        return () => {
            shutdown();
        };
    }, [isB2CBrand]);

    return null;
};

export default IntercomMessenger;
